import React, { useState } from 'react';
import { Button, OptimizedImage } from '../common';
import { submitToGoogleScript } from '../../services/api';
import { CheckCircle, Mail, MapPin, Clock } from 'lucide-react';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  subject: string;
  message: string;
}

export const ContactFormSection: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await submitToGoogleScript('contact', formData as unknown as Record<string, unknown>);
      
      if (response.status === 'success') {
        setIsSuccess(true);
      } else {
        // Show error message to user
        setErrors({ submit: response.message || 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <>
        {/* Hero Section with background image */}
        <section className="relative px-4 md:px-16 py-16 md:py-24 lg:py-28 bg-Color-Neutral-Darkest overflow-hidden">
          <div className="absolute inset-0 z-0">
            <OptimizedImage
              src="/images/022.contact.jpeg"
              alt="Thank you for contacting us"
              className="absolute inset-0 w-full h-full"
              priority={true}
              sizes="100vw"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
          
          <div className="container mx-auto relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-24 h-24 bg-[#C97B5A] rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl">
                <CheckCircle className="w-12 h-12 text-white" strokeWidth={2.5} />
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold font-gabarito text-white mb-6 tracking-tight">
                Message <span className="text-[#C97B5A]">Sent!</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-white mb-12 font-cairo leading-relaxed">
                {import.meta.env.VITE_CONTACT_SCRIPT_URL 
                  ? "Thank you for reaching out! We've received your message and will get back to you within 24 hours."
                  : "This is a demo submission. To enable actual form submissions, please configure the Google Apps Script URL in your environment variables."
                }
              </p>
            </div>
          </div>
        </section>

        {/* What happens next section */}
        <section className="px-4 md:px-16 py-16 md:py-20 bg-Color-Scheme-1-Background">
          <div className="container mx-auto">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold font-gabarito text-Color-Scheme-1-Text mb-12 text-center">
                What happens <span className="text-[#C97B5A]">next?</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#F5E6D3] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-[#C97B5A] font-gabarito">1</span>
                  </div>
                  <h3 className="text-xl font-bold font-gabarito text-Color-Scheme-1-Text mb-2">Quick Review</h3>
                  <p className="text-Color-Neutral-Dark font-cairo">Our team reviews your message within 2 hours</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#F5E6D3] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-[#C97B5A] font-gabarito">2</span>
                  </div>
                  <h3 className="text-xl font-bold font-gabarito text-Color-Scheme-1-Text mb-2">Personal Response</h3>
                  <p className="text-Color-Neutral-Dark font-cairo">We prepare a personalized response to your inquiry</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#F5E6D3] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-[#C97B5A] font-gabarito">3</span>
                  </div>
                  <h3 className="text-xl font-bold font-gabarito text-Color-Scheme-1-Text mb-2">Fast Reply</h3>
                  <p className="text-Color-Neutral-Dark font-cairo">You receive our reply within 24 hours</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="primary" size="lg" onClick={() => window.location.href = '/products'}>
                  Explore Our Products
                </Button>
                <Button variant="secondary" size="lg" onClick={() => window.location.href = '/'}>
                  Back to Home
                </Button>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <section className="px-4 md:px-16 py-16 md:py-24 lg:py-28 bg-Color-Scheme-1-Background">
      <div className="container mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="mb-6 text-5xl md:text-6xl lg:text-7xl font-bold font-gabarito text-Color-Scheme-1-Text leading-tight">
            Get in <span className="text-[#C97B5A]">Touch</span>
          </h1>
          <p className="text-lg md:text-xl text-Color-Scheme-1-Text font-cairo max-w-3xl mx-auto">
            Want to learn more about MindClave?<br />
            We'd love to hear from you!
          </p>
        </div>
        
        {/* Form Section */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[#C97B5A] to-[#B06747] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold font-gabarito text-Color-Neutral-Darkest mb-2">
                Send us a message
              </h2>
              {!import.meta.env.VITE_CONTACT_SCRIPT_URL && (
                <p className="text-sm text-Color-Neutral-Dark font-cairo mb-4">
                  (Demo mode - form submissions are simulated)
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold text-Color-Neutral-Darker mb-2 font-cairo">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-200 focus:ring-4 focus:ring-[#C97B5A]/20 focus:border-[#C97B5A] font-cairo ${
                      errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  />
                  {errors.firstName && (
                    <p className="mt-2 text-sm text-red-600 font-cairo">{errors.firstName}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold text-Color-Neutral-Darker mb-2 font-cairo">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Doe"
                    className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-200 focus:ring-4 focus:ring-[#C97B5A]/20 focus:border-[#C97B5A] font-cairo ${
                      errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  />
                  {errors.lastName && (
                    <p className="mt-2 text-sm text-red-600 font-cairo">{errors.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-Color-Neutral-Darker mb-2 font-cairo">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-200 focus:ring-4 focus:ring-[#C97B5A]/20 focus:border-[#C97B5A] font-cairo ${
                    errors.email ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600 font-cairo">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-Color-Neutral-Darker mb-2 font-cairo">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="I'm interested in your AI solutions"
                  className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-200 focus:ring-4 focus:ring-[#C97B5A]/20 focus:border-[#C97B5A] font-cairo ${
                    errors.subject ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                />
                {errors.subject && (
                  <p className="mt-2 text-sm text-red-600 font-cairo">{errors.subject}</p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-Color-Neutral-Darker mb-2 font-cairo">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us more about your needs and how we can help you..."
                  rows={5}
                  className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-200 focus:ring-4 focus:ring-[#C97B5A]/20 focus:border-[#C97B5A] font-cairo resize-none ${
                    errors.message ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                />
                {errors.message && (
                  <p className="mt-2 text-sm text-red-600 font-cairo">{errors.message}</p>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending Message...' : 'Send Message'}
              </Button>
              
              {errors.submit && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 text-center font-cairo">{errors.submit}</p>
                </div>
              )}
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-12 text-center space-y-6">
            <div className="flex items-center justify-center space-x-2 text-Color-Neutral-Dark">
              <Mail className="w-5 h-5" />
              <span className="font-medium font-cairo">Email:</span>
              <a href="mailto:hello@mindclave.com" className="text-[#C97B5A] hover:text-[#B06747] font-semibold font-cairo">
                hello@mindclave.com
              </a>
            </div>
            
            <div className="flex items-center justify-center space-x-2 text-Color-Neutral-Dark">
              <MapPin className="w-5 h-5" />
              <span className="font-medium font-cairo">Location:</span>
              <span className="font-semibold font-cairo">Cambridge, MA</span>
            </div>
            
            <div className="flex items-center justify-center space-x-2 text-Color-Neutral-Dark">
              <Clock className="w-5 h-5" />
              <span className="font-medium font-cairo">•</span>
              <span className="text-green-600 font-semibold font-cairo">Usually responds within 24 hours</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};